import React, {useState, useEffect} from 'react';
import Condition from './Condition';
import {plusBlue, trashDarkRedTwo} from 'assets/images/images';
import {ChevronDown, ChevronUp} from 'lucide-react';
import {
  updateGroupConditionType,
  deleteGroup,
  addNewCondition,
} from '../hooks/ruleUtils';

function Group({ruleId, group, rules, setRules, index}) {
  const [isOpen, setIsOpen] = useState(true);
  const [isConditionsOpen, setIsConditionsOpen] = useState(true);

  const canDelete = rules.find(rule => rule.id === ruleId)?.groups.length > 1;
  const conditionCount = group.conditions.length || 0;

  const handleAddNewCondition = () => {
    addNewCondition(rules, setRules, ruleId, group.id);
  };

  const handleDeleteGroup = () => {
    if (canDelete) {
      deleteGroup(rules, setRules, ruleId, group.id);
    }
  };

  const handleUpdateConditionType = React.useCallback(
    conditionType => {
      updateGroupConditionType(
        rules,
        setRules,
        ruleId,
        group.id,
        conditionType,
      );
    },
    [rules, setRules, ruleId, group.id],
  );

  useEffect(() => {
    if (group.conditionType === undefined) {
      handleUpdateConditionType('and');
    }
  }, [group.id, group.conditionType, handleUpdateConditionType]);

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {canDelete && (
            <button
              onClick={e => {
                e.stopPropagation();
                handleDeleteGroup();
              }}
              className="text-red-500"
              title="Delete group"
            >
              <img src={trashDarkRedTwo} alt="Delete" />
            </button>
          )}
          <p className="text-[12px] text-[#73739B] font-medium  ">GROUP</p>
          <div className="h-[1px] w-[170px] bg-[#DEDEE3]"></div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleAddNewCondition}
            className="text-xs font-medium flex items-center gap-1 outline-none text-[#033971]"
          >
            <img src={plusBlue} className="w-3 h-3" alt="Add" />
            Add condition
          </button>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className="focus:outline-none"
          >
            {isOpen ? (
              <ChevronUp className="w-5 h-5 fill-black stroke-none" />
            ) : (
              <ChevronDown className="w-5 h-5 fill-black stroke-none" />
            )}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="bg-[#F9F9F9] p-4 rounded-md">
          <div
            className="flex items-center justify-between mb-3 cursor-pointer"
            onClick={() => setIsConditionsOpen(!isConditionsOpen)}
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[#080917]">
                Condition{' '}
              </span>
              {conditionCount > 1 && (
                <span className="text-[8px]   font-medium text-white flex items-center justify-center bg-brandBlue w-4 h-4 rounded-full">
                  {conditionCount}
                </span>
              )}
            </div>
            <button className="focus:outline-none">
              {isConditionsOpen ? (
                <ChevronUp className="w-5 h-5 fill-black stroke-none" />
              ) : (
                <ChevronDown className="w-5 h-5 fill-black stroke-none" />
              )}
            </button>
          </div>

          {isConditionsOpen && (
            <>
              <p className="text-[11px] text-[#4A4A68] mb-4">
                Set customizable rules and conditions to spot fraud through your
                user&apos;s journey
              </p>
              {conditionCount > 1 && (
                <div className="mb-4">
                  <label className="mb-2 block font-medium">
                    Condition Type
                  </label>
                  <div className="flex">
                    <label
                      className={`flex flex-row items-center px-4 py-2 w-[80px] rounded-l-full border cursor-pointer ${
                        group.conditionType === 'and'
                          ? 'bg-brandBlue text-white border-none'
                          : 'bg-white text-[#033971] border-[#DFE1E7]'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`groupConditionType-${group.id}`}
                        value="and"
                        checked={group.conditionType === 'and'}
                        onChange={() => handleUpdateConditionType('and')}
                        className="mr-2 w-4 h-4 !accent-black"
                      />
                      <span>And</span>
                    </label>

                    <label
                      className={`flex flex-row items-center px-4 py-2 w-[80px] rounded-r-full border cursor-pointer ${
                        group.conditionType === 'or'
                          ? 'bg-brandBlue text-white border-none'
                          : 'bg-white text-[#033971] border-[#DFE1E7]'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`groupConditionType-${group.id}`}
                        value="or"
                        checked={group.conditionType === 'or'}
                        onChange={() => handleUpdateConditionType('or')}
                        className="mr-2 w-4 h-4 !accent-black"
                      />
                      <span>Or</span>
                    </label>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-2">
                {group.conditions.map((condition, index) => (
                  <Condition
                    key={condition.id}
                    ruleId={ruleId}
                    groupId={group.id}
                    condition={condition}
                    rules={rules}
                    setRules={setRules}
                    index={index}
                    conditionType={group.conditionType || 'and'}
                    onUpdateConditionType={handleUpdateConditionType}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Group;
